import PropTypes from 'prop-types';

import { Box, Button, Typography } from '@mui/material';

import BackButtonComponent from '../../Forms/BackButtonComponent';
import FormFieldComponent from '../../Forms/FormFieldComponent';

import { maxFileSize, notNullOrUndefined, required } from '../../Forms/utils';
import { getDisabledScheduleValues } from '../../Schedule/utils';

import { CATEGORIES_LIST } from '../constants';

import {
  CHECKBOX,
  DATE_PICKER,
  IMAGE_UPLOAD,
  REACT_SELECT_INPUT,
  STYLED_TEXT,
  TIME_PICKER,
} from '../../Forms/fieldTypes';

const EventForm = (props) => {
  const {
    isCreate,
    onSubmit,
    formError,
    isLoading,
    isSkeletonDisabled,
    isAllowGuests,
    isCustomLocation,
    isLimitAttendees,
    buildings,
    buildingId,
    amenityId,
    startTime,
    endTime,
    amenities,
  } = props;

  const fieldStateProps = { isLoading, isSkeletonDisabled };

  return (
    <Box my="1rem" sx={{ overflowWrap: 'anywhere' }}>
      <form onSubmit={onSubmit} method="post" autoComplete="off">
        {formError && (
          <Typography color="secondary" variant="caption">
            {formError}
          </Typography>
        )}
        <FormFieldComponent
          fieldType={STYLED_TEXT}
          keyName="name"
          label="Event name"
          validate={[notNullOrUndefined]}
          {...fieldStateProps}
        />
        <FormFieldComponent
          fieldType={STYLED_TEXT}
          keyName="organizer"
          label="Organizer"
          validate={[notNullOrUndefined]}
          {...fieldStateProps}
        />
        <FormFieldComponent
          fieldType={DATE_PICKER}
          keyName="startDate"
          label="Start date"
          disabled={false}
          validate={[notNullOrUndefined]}
          {...fieldStateProps}
        />
        <Box display="flex" gap="1rem">
          <FormFieldComponent
            fieldType={TIME_PICKER}
            timeFormat="h:mm a"
            use12Hours
            keyName="startTime"
            label="Start time"
            validate={[notNullOrUndefined]}
            disabled={false}
            getDisabledHours={() => {}}
            {...fieldStateProps}
          />
          <FormFieldComponent
            fieldType={TIME_PICKER}
            timeFormat="h:mm a"
            use12Hours
            keyName="endTime"
            label="End time"
            validate={[notNullOrUndefined]}
            disabled={false}
            getDisabledHours={() =>
              getDisabledScheduleValues('hour', 'endTime', startTime, endTime)
            }
            getDisabledMinutes={(selectedHour) =>
              getDisabledScheduleValues(
                'minute',
                'endTime',
                startTime,
                endTime,
                selectedHour,
              )
            }
            {...fieldStateProps}
          />
        </Box>
        <FormFieldComponent
          fieldType={REACT_SELECT_INPUT}
          keyName="buildingId"
          options={buildings}
          disabled={!isCreate}
          label="Building"
          validate={[required]}
          {...fieldStateProps}
        />

        {buildingId && (
          <FormFieldComponent
            fieldType={REACT_SELECT_INPUT}
            keyName="amenityId"
            options={amenities}
            label="Location"
            placeholder="Choose amenity"
            disabled={isCustomLocation || !buildingId}
            validate={[]}
            {...fieldStateProps}
          />
        )}

        {buildingId && (
          <>
            <FormFieldComponent
              fieldType={CHECKBOX}
              keyName="customLocationCheckBox"
              label="Custom location"
              validate={[notNullOrUndefined]}
              {...fieldStateProps}
            />
            {isCustomLocation && (
              <FormFieldComponent
                fieldType={STYLED_TEXT}
                keyName="locationDescription"
                label="Custom location"
                validate={[required]}
                disabled={amenityId}
                placeholder="Enter custom location"
                {...fieldStateProps}
              />
            )}
          </>
        )}

        <FormFieldComponent
          fieldType={STYLED_TEXT}
          keyName="description"
          minRows={5}
          multiline
          label="Event description"
          validate={[notNullOrUndefined]}
          placeholder="Enter event description"
          {...fieldStateProps}
        />
        <FormFieldComponent
          fieldType={REACT_SELECT_INPUT}
          keyName="category"
          options={CATEGORIES_LIST}
          label="Category"
          sortAlphabetically={false}
          placeholder="Select category"
          validate={[required]}
          {...fieldStateProps}
        />

        <Box width="20%">
          <FormFieldComponent
            fieldType={IMAGE_UPLOAD}
            keyName="image"
            label="Image"
            validate={[maxFileSize]}
            {...fieldStateProps}
          />
        </Box>
        <FormFieldComponent
          fieldType={CHECKBOX}
          keyName="allowGuests"
          label="Allow residents to bring guests"
          validate={[notNullOrUndefined]}
          {...fieldStateProps}
        />

        <Box width="15%">
          <FormFieldComponent
            fieldType={STYLED_TEXT}
            keyName="guestsPerResidentLimit"
            label="Max guests per resident"
            validate={[notNullOrUndefined]}
            isHidden={!isAllowGuests}
            {...fieldStateProps}
          />
        </Box>

        <FormFieldComponent
          fieldType={CHECKBOX}
          keyName="limitAttendees"
          label="Limit event attendees"
          validate={[notNullOrUndefined]}
          {...fieldStateProps}
        />

        <Box width="15%">
          <FormFieldComponent
            fieldType={STYLED_TEXT}
            keyName="capacity"
            label="Max attendees"
            validate={[notNullOrUndefined]}
            isHidden={!isLimitAttendees}
            {...fieldStateProps}
          />
        </Box>

        <Box display="flex">
          <Button
            sx={{ marginRight: '1rem' }}
            type="submit"
            variant="contained"
            color="primary"
            disableElevation
          >
            Submit
          </Button>
          <BackButtonComponent text="Cancel" />
        </Box>
      </form>
    </Box>
  );
};

EventForm.propTypes = {
  isCreate: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  formError: PropTypes.string,
  isLoading: PropTypes.bool,
  isSkeletonDisabled: PropTypes.bool,
  isAllowGuests: PropTypes.bool,
  isCustomLocation: PropTypes.bool,
  isLimitAttendees: PropTypes.bool,
  buildings: PropTypes.instanceOf(Array).isRequired,
  buildingId: PropTypes.number,
  amenityId: PropTypes.number,
  startTime: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Object),
  ]),
  endTime: PropTypes.string,
  amenities: PropTypes.instanceOf(Object),
};

export default EventForm;
